import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import logger from '../utils/logger';
import branchReloadObserver from '../utils/branchReloadObserver';
import { resolveBranchScopes } from '../utils/branchScopeResolver';

export const BranchContext = createContext();

export const BranchProvider = ({ children, user, onBranchChange, onBranchVersionChange }) => {
  const [currentBranch, setCurrentBranch] = useState(null);
  const [allowedBranches, setAllowedBranches] = useState([]);
  const [isGlobalAdmin, setIsGlobalAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // A simple token/version that increments whenever branch-related state changes.
  // Consumers can subscribe to this to coordinate deterministic reloads.
  const [branchVersion, setBranchVersion] = useState(() => Date.now());

  // Initialize branch data from user
  useEffect(() => {
    const initializeBranches = async () => {
      if (!user?.uid) {
        setLoading(false);
        return;
      }

      logger.debug('BranchContext: initialize - starting', { uid: user.uid });

      try {
        const normalize = (value) => String(value || '').trim().toLowerCase();

        // Hardcoded canonical overrides: ANY of these aliases always resolves to the canonical ID.
        // This is the source of truth regardless of what is stored in claims or user docs.
        const CANONICAL_OVERRIDES = {
          'y6yv2avw6ujwg7dbfm2q': 'Krinkels Heerlen',
          'heerlen': 'Krinkels Heerlen',
          'krinkels-heerlen': 'Krinkels Heerlen',
          'krinkels heerlen': 'Krinkels Heerlen',
        };
        const applyCanonicalOverride = (raw) => {
          const key = normalize(raw || '');
          return CANONICAL_OVERRIDES[key] || raw;
        };

        // Prefer custom claims (they drive Firestore security rules)
        let claims = {};
        try {
          const tokenResult = await user.getIdTokenResult(true);
          claims = tokenResult?.claims || {};
        } catch {
          claims = {};
        }

        const claimsIsGlobalAdmin = claims.isGlobalAdmin === true || claims.isSuperAdmin === true;
        const claimsBranchId = claims.branchId ? applyCanonicalOverride(claims.branchId) : null;
        const claimsBranchesAllowed = Array.isArray(claims.branchesAllowed)
          ? claims.branchesAllowed.map(applyCanonicalOverride)
          : null;

        const allBranchesSnap = await getDocs(collection(db, 'branches'));
        const allBranches = allBranchesSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        const activeBranches = allBranches.filter((b) => b.active !== false);

        // Get user document
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          logger.debug('BranchContext: got userDoc', {
            uid: user.uid,
            branchId: userData.branchId,
            isGlobalAdmin: userData.isGlobalAdmin,
            branchesAllowed: userData.branchesAllowed
          });

          // Resolve display names and branch aliases to the real vestigingen document IDs.
          const effectiveBranchIdRaw = claimsBranchId || userData.branchId || null;
          const rawBranchIds = claimsBranchesAllowed
            || (Array.isArray(userData.branchesAllowed) ? userData.branchesAllowed : [effectiveBranchIdRaw]);
          const branchResolution = await resolveBranchScopes([
            effectiveBranchIdRaw,
            ...rawBranchIds,
            ...activeBranches.map((branch) => branch.id),
          ]);
          const scopeFor = (raw) => branchResolution.scopes.find((scope) => scope.input === String(raw || '').trim());
          const effectiveScope = scopeFor(effectiveBranchIdRaw);
          const effectiveBranchId = effectiveScope?.branchDocumentId;
          if (!effectiveBranchId) throw new Error('User has no branchId assigned (claims or user doc)');
          setCurrentBranch(effectiveBranchId);
          // notify parent (App) if provided
          try { onBranchChange && onBranchChange(effectiveBranchId); } catch (e) { logger.warn('BranchContext: onBranchChange callback failed', e); }
          setIsGlobalAdmin(claimsIsGlobalAdmin || userData.isGlobalAdmin === true || userData.isSuperAdmin === true);

          // If global admin (claims), fetch all branches
          if (claimsIsGlobalAdmin) {
            try {
              const branches = activeBranches.map((branch) => {
                const scope = scopeFor(branch.id) || scopeFor(branch.name);
                return scope
                  ? { ...branch, id: scope.branchDocumentId, branchDocumentId: scope.branchDocumentId, branchName: scope.branchName }
                  : null;
              }).filter(Boolean);
              setAllowedBranches(branches.length > 0 ? branches : [{ id: effectiveBranchId, name: 'Default Branch' }]);
              // bump version so consumers reload deterministically
              {
                const v = Date.now();
                setBranchVersion(v);
                branchReloadObserver.logBranchReload(v, effectiveBranchId, 'load_all_branches');
                try { onBranchVersionChange && onBranchVersionChange(v); } catch (e) { logger.warn('BranchContext: onBranchVersionChange callback failed', e); }
              }
            } catch (e) {
              logger.warn('BranchContext: Error fetching branches:', e);
              setAllowedBranches([{ id: effectiveBranchId, name: 'Default Branch' }]);
            }
          } else {
            // User-specific branches
            const branchesData = Array.from(new Map(
              rawBranchIds.map((raw) => {
                const scope = scopeFor(raw);
                const branch = allBranches.find((item) => item.id === raw || normalize(item.name) === normalize(raw));
                return scope ? [scope.branchDocumentId, {
                  ...(branch || {}),
                  id: scope.branchDocumentId,
                  branchDocumentId: scope.branchDocumentId,
                  branchName: scope.branchName,
                  name: scope.branchName,
                }] : null;
              }).filter(Boolean),
            ).values());
            if (!branchesData.some((branch) => branch.id === effectiveBranchId)) {
              branchesData.push({ id: effectiveBranchId, branchDocumentId: effectiveBranchId, name: effectiveScope.branchName });
            }

            setAllowedBranches(branchesData.length > 0 ? branchesData : [{ id: effectiveBranchId, name: 'Default Branch' }]);
            // bump version so consumers reload deterministically
            {
              const v = Date.now();
              setBranchVersion(v);
              branchReloadObserver.logBranchReload(v, effectiveBranchId, 'load_user_branches');
              try { onBranchVersionChange && onBranchVersionChange(v); } catch (e) { logger.warn('BranchContext: onBranchVersionChange callback failed', e); }
            }
          }
        } else {
          // User doc doesn't exist - cannot proceed
          throw new Error('User document not found');
        }

        setError(null);
      } catch (err) {
        logger.error('BranchContext: Error initializing branches:', err);
        setError(err.message);
        // Cannot set defaults, leave as null
        setCurrentBranch(null);
        setAllowedBranches([]);
        logger.error('BranchContext: initialization error', err.message);
      } finally {
        setLoading(false);
      }
    };

    initializeBranches();
  }, [user?.uid]);

  // Switch to different branch
  const switchBranch = useCallback((branchId) => {
    // Apply canonical override to ensure Heerlen aliases resolve to 'Krinkels Heerlen'
    const normalize = (value) => String(value || '').trim().toLowerCase();
    const CANONICAL_OVERRIDES = {
      'y6yv2avw6ujwg7dbfm2q': 'Krinkels Heerlen',
      'heerlen': 'Krinkels Heerlen',
      'krinkels-heerlen': 'Krinkels Heerlen',
      'krinkels heerlen': 'Krinkels Heerlen',
    };
    const normalizedBranchId = CANONICAL_OVERRIDES[normalize(branchId)] || branchId;

    // Check if user has access to this branch
    if (allowedBranches.some(b => b.id === normalizedBranchId) || isGlobalAdmin) {
      setCurrentBranch(normalizedBranchId);
      localStorage.setItem(`branch_${user?.uid}`, normalizedBranchId);
      // bump version and notify parent
      {
        const v = Date.now();
        setBranchVersion(v);
        branchReloadObserver.logBranchReload(v, normalizedBranchId, 'switch');
        try { onBranchVersionChange && onBranchVersionChange(v); } catch (e) { logger.warn('BranchContext: onBranchVersionChange callback failed', e); }
      }
      try { onBranchChange && onBranchChange(normalizedBranchId); } catch (e) { logger.warn('BranchContext: onBranchChange callback failed', e); }
    } else {
      console.warn(`User does not have access to branch: ${normalizedBranchId}`);
    }
  }, [allowedBranches, isGlobalAdmin, user?.uid]);

  // Check if user has access to branch
  const isAllowed = useCallback((branchId) => {
    if (isGlobalAdmin) return true;
    return allowedBranches.some(b => b.id === branchId);
  }, [allowedBranches, isGlobalAdmin]);

  // Get branch by ID
  const getBranchById = useCallback((branchId) => {
    return allowedBranches.find(b => b.id === branchId);
  }, [allowedBranches]);

  // Get current branch data
  const getCurrentBranch = useCallback(() => {
    return allowedBranches.find(b => b.id === currentBranch);
  }, [allowedBranches, currentBranch]);

  const value = useMemo(() => ({
    currentBranch,
    allowedBranches,
    isGlobalAdmin,
    branchVersion,
    loading,
    error,
    switchBranch,
    isAllowed,
    getBranchById,
    getCurrentBranch
  }), [currentBranch, allowedBranches, isGlobalAdmin, branchVersion, loading, error, switchBranch, isAllowed, getBranchById, getCurrentBranch]);

  return (
    <BranchContext.Provider value={value}>
      {children}
    </BranchContext.Provider>
  );
};
